import { Box, Button, Flex, IconButton, Table, Tooltip } from '@radix-ui/themes';
import PropTypes from 'prop-types';
import React from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import TextWithTooltip from '~/components/TextWithTooltip';
import getProp from '~/lib/get-prop';
import { actionsType, orderType } from './types';

function getEditButton(url, data, onClickEdit, editLinkFn, label) {
  const buttonElement = (
    <Box
      width='40px'
      asChild
    >
      <Button
        color='gray'
        variant='outline'
        radius='full'
        size='1'
        onClick={() => !!onClickEdit && onClickEdit(data)}
      >
        {label}
      </Button>
    </Box>
  );

  if (!editLinkFn) {
    return buttonElement;
  }

  return <NavLink to={editLinkFn(url, data)}>{buttonElement}</NavLink>;
}

function RenderButtons({ data, actions, onClickEdit, editLinkFn, goToFn, goToLinkFn, showEditButton, showViewButton }) {
  const { url } = useRouteMatch();
  const buttons = [];

  if (showEditButton) buttons.push(getEditButton(url, data, onClickEdit, editLinkFn, 'Edit'));
  if (showViewButton) buttons.push(getEditButton(url, data, onClickEdit, editLinkFn, 'View'));

  if (goToLinkFn) {
    buttons.push(
      <NavLink to={goToLinkFn(url, data)}>
        <Button
          key='goToLink'
          color='gray'
          variant='outline'
          radius='full'
          size='1'
          data-intercom-target='sim_row_view_button'
        >
          View
        </Button>
      </NavLink>,
    );
  }

  if (goToFn) {
    buttons.push(
      <Button
        key='goToFn'
        color='gray'
        variant='outline'
        radius='full'
        size='1'
        onClick={() => goToFn(url, data)}
      >
        View
      </Button>,
    );
  }

  if (actions?.length > 0) {
    buttons.push(
      ...actions.map(action => {
        function ButtonElement() {
          const buttonProps = {
            color: action.color || 'gray',
            variant: action.variant || 'outline',
            size: '1',
            onClick: () => action.onClick?.(data),
          };

          if (!action.icon) {
            return (
              <Button
                {...buttonProps}
                radius='full'
              >
                {action.label}
              </Button>
            );
          }

          if (!action.label) {
            return <IconButton {...buttonProps}>{action.icon}</IconButton>;
          }

          return (
            <Tooltip content={action.label}>
              <IconButton {...buttonProps}>{action.icon}</IconButton>
            </Tooltip>
          );
        }

        if (action.getUrl) {
          return (
            <Flex
              key={action.key}
              height='100%'
              align='center'
              asChild
            >
              <NavLink to={action.getUrl(data)}>
                <ButtonElement />
              </NavLink>
            </Flex>
          );
        }
        return <ButtonElement key={action.key} />;
      }),
    );
  }

  return (
    <Flex
      align='center'
      gap='3'
    >
      {buttons}
    </Flex>
  );
}

RenderButtons.propTypes = {
  data: PropTypes.object.isRequired,
  actions: actionsType,
  url: PropTypes.string.isRequired,
  onClickEdit: PropTypes.func,
  editLinkFn: PropTypes.func,
  goToFn: PropTypes.func,
  goToLinkFn: PropTypes.func,
  showEditButton: PropTypes.bool,
  showViewButton: PropTypes.bool,
};

function TableRow({
  data,
  columnsToRender,
  actions = [],
  onClickEdit,
  editLinkFn,
  goToFn,
  goToLinkFn,
  canWrite,
  fallbackEditToView,
  hideEditButton,
  hasRowButtons,
  metaData,
}) {
  const showEditButton = Boolean(!hideEditButton && (data.canEdit ?? canWrite));
  const showViewButton = !showEditButton && fallbackEditToView;

  const showButtonsCell =
    hasRowButtons || showEditButton || showViewButton || goToLinkFn || goToFn || actions.length > 0;

  return (
    <Table.Row>
      {columnsToRender.map(column => {
        return (
          <Table.Cell
            key={column.key}
            width={column.shrink ? '1%' : 'auto'}
          >
            <Flex
              height='100%'
              align='center'
            >
              {column.valueRenderFunc ? (
                <Box maxWidth={column.maxWidth}>{column.valueRenderFunc(data, metaData)}</Box>
              ) : (
                <TextWithTooltip maxWidth={`${column.maxWidth}px`}>{getProp(column.key, data)}</TextWithTooltip>
              )}
            </Flex>
          </Table.Cell>
        );
      })}
      {!!showButtonsCell && (
        <Table.Cell width='1%'>
          <Flex
            height='100%'
            justify='center'
          >
            <RenderButtons
              data={data}
              actions={actions}
              onClickEdit={onClickEdit}
              editLinkFn={editLinkFn}
              goToFn={goToFn}
              goToLinkFn={goToLinkFn}
              showEditButton={showEditButton}
              showViewButton={showViewButton}
            />
          </Flex>
        </Table.Cell>
      )}
    </Table.Row>
  );
}

TableRow.propTypes = {
  ...RenderButtons.propTypes,
  order: orderType.isRequired,
  hasRowButtons: PropTypes.bool,
};

const MemoizedTableRow = React.memo(TableRow, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.columnsToRender === nextProps.columnsToRender &&
    prevProps.metaData === nextProps.metaData
  );
});

export default MemoizedTableRow;
